import {
  Backdrop,
  Button,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import mainStyles from "../WorkerManagementComponent/WorkerManagementComponent.module.css";
import styles from "../AgentsComponent/styles.module.css";
import { Add, DeleteForever } from "@material-ui/icons";
import CreateIcon from "@material-ui/icons/Create";
import PersonIcon from "@material-ui/icons/Person";
import { useEffect, useState } from "react";
import { TeamsActions } from "../../actions/TeamsActions";
import { IExternalAgent } from "./IExternalAgent";
import {
  ExternalAgentsActions,
  describeAgentError,
} from "../../actions/ExternalAgentsActions";
import { BlueInverseBtn } from "../Styled/StyledButtons";
import ConfirmComponent from "../ConfirmComponent/ConfirmComponent";
import history from "../history";

/**
 * External agents — people who take call-center tasks without being Apollo users.
 *
 * They exist as Twilio TaskRouter workers with the same skills, departments and KPI flags an
 * internal agent carries, which is what lets a workflow route to them, so this screen is a
 * near-mirror of Agent Skills Management for a population that has no CRM account.
 *
 * Availability is owned by TaskRouter, not by us, and is therefore three-valued: enabled,
 * disabled, or not known — an agent whose worker was never created, or a workspace we could
 * not reach. Rendering "unknown" as "Disabled" would invite an operator to fix a routing
 * problem that is not the one they have.
 */
const ExternalAgents = () => {
  const [teams, setTeams] = useState<Array<{ Id: number; Name: string }>>([]);
  const [backdrop, setBackdrop] = useState(true);
  const [agents, setAgents] = useState<IExternalAgent[]>([]);
  const [pendingDelete, setPendingDelete] = useState<IExternalAgent | null>(null);
  const [alert, setAlert] = useState<{
    severity: "error" | "success";
    text: string;
  } | null>(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setBackdrop(true);
    try {
      // Independent reads — the team names are a lookup for this list, not a dependency of
      // the agent fetch, so there is no reason to pay for them in sequence.
      const [loadedTeams, loadedAgents] = await Promise.all([
        TeamsActions.getTeams(),
        ExternalAgentsActions.get(),
      ]);
      setTeams(loadedTeams);
      setAgents(loadedAgents);
    } catch (error) {
      setAlert({ severity: "error", text: describeAgentError(error) });
    } finally {
      // Inside the async function. This used to sit after an un-awaited fetchData(), so it
      // ran before the first request had even been issued and the loader never appeared.
      setBackdrop(false);
    }
  };

  const teamName = (teamId: number | null): string => {
    if (teamId === null) {
      return "";
    }
    const team = teams.find((t) => t.Id === teamId);
    // The id is a poor answer but a better one than a blank cell if a team was deleted.
    return team ? team.Name : String(teamId);
  };

  const toggleHandler = async (agent: IExternalAgent) => {
    setBackdrop(true);
    try {
      const result = await ExternalAgentsActions.toggle(agent.id!);
      // Patch the one row rather than refetching the list: a refetch re-reads every worker
      // in the workspace, and the toggle response already carries the new state.
      setAgents((current) =>
        current.map((row) =>
          row.id === agent.id ? { ...row, available: result.available } : row
        )
      );
    } catch (error) {
      setAlert({ severity: "error", text: describeAgentError(error) });
    } finally {
      setBackdrop(false);
    }
  };

  const deleteHandler = async () => {
    const agent = pendingDelete;
    setPendingDelete(null);

    if (!agent || agent.id === undefined) {
      return;
    }

    setBackdrop(true);
    try {
      await ExternalAgentsActions.delete(agent.id);
      setAgents((current) => current.filter((row) => row.id !== agent.id));
      setAlert({ severity: "success", text: `${agent.name} deleted.` });
    } catch (error) {
      setAlert({ severity: "error", text: describeAgentError(error) });
    } finally {
      setBackdrop(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <Grid container spacing={1}>
        <Grid item xs={12} className={mainStyles.WorkerSkillsBack}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <div className={mainStyles.Header}>
                External Agents Management
              </div>
              <div className={mainStyles.AgentName}> &nbsp;</div>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={12} md={3}>
              <BlueInverseBtn
                onClick={() => history.push("/external-agent-create")}
                variant="contained"
                fullWidth={true}
              >
                <Add /> Add Agent
              </BlueInverseBtn>
            </Grid>
          </Grid>
        </Grid>
        <TableContainer component={Paper}>
          <Table aria-label="external agents">
            <TableHead>
              <TableRow>
                <TableCell
                  align="right"
                  className={styles.AgentIcon}
                ></TableCell>
                <TableCell align="left">Name</TableCell>
                <TableCell align="center">Team</TableCell>
                <TableCell align="center">Active</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={mainStyles.FlexColScroll}>
              {agents.length === 0 && !backdrop ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <span className={styles.GreyText}>No external agents yet.</span>
                  </TableCell>
                </TableRow>
              ) : null}
              {agents.map((row) => (
                <TableRow key={row.id}>
                  <TableCell align="right" className={styles.AgentIcon}>
                    <PersonIcon />
                  </TableCell>
                  <TableCell align="left">
                    <b>{row.name}</b>
                    <br />
                    {row.phone}
                  </TableCell>
                  <TableCell align="center">{teamName(row.team_id)}</TableCell>
                  <TableCell align="center">
                    {row.available === null ? (
                      <span className={styles.GreyText}>
                        {row.sid ? "Unknown" : "No worker"}
                      </span>
                    ) : (
                      <div className={styles.SwitchContainer}>
                        <div className={styles.Switch}>
                          <input
                            type="radio"
                            id={"switch_left_" + row.id}
                            name={"switch" + row.id}
                            value="0"
                            checked={!row.available}
                            onChange={() => toggleHandler(row)}
                          />
                          <label htmlFor={"switch_left_" + row.id}>
                            Disabled
                          </label>
                          <input
                            type="radio"
                            id={"switch_right_" + row.id}
                            name={"switch" + row.id}
                            value="1"
                            checked={row.available}
                            onChange={() => toggleHandler(row)}
                          />
                          <label htmlFor={"switch_right_" + row.id}>
                            Enabled
                          </label>
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      color="default"
                      onClick={() => history.push("/external-agent/id/" + row.id)}
                    >
                      <CreateIcon />
                    </Button>

                    <Button
                      color="secondary"
                      onClick={() => setPendingDelete(row)}
                    >
                      <DeleteForever />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      <ConfirmComponent
        show={pendingDelete !== null}
        title={"Delete " + (pendingDelete ? pendingDelete.name : "")}
        bodyText="This removes the agent and its Twilio worker. Tasks already assigned are not reassigned."
        close={() => setPendingDelete(null)}
        confirm={() => deleteHandler()}
      />
      <Snackbar
        open={alert !== null}
        autoHideDuration={6000}
        onClose={() => setAlert(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={() => setAlert(null)}
          severity={alert ? alert.severity : "success"}
          variant="filled"
        >
          {alert ? alert.text : ""}
        </Alert>
      </Snackbar>
      <Backdrop className={styles.Backdrop} open={backdrop}>
        <CircularProgress className={styles.BackdropProgress} />
        &nbsp;&nbsp;
        <p className={styles.BackdropProgress}>Loading...</p>
      </Backdrop>
    </Container>
  );
};

export default ExternalAgents;
